export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type BreakpointKey = keyof typeof breakpoints;

/**
 * Layout tiers — the four shapes an app shell is actually designed for.
 *
 * `breakpoints` above is the generic Tailwind-shaped scale; these are the
 * subset the shell and its content agree on, named after what changes rather
 * than after a t-shirt size. Anything that has to make the same decision in
 * both CSS and TypeScript reads it from here, so the two can't drift — that
 * drift is the whole reason this exists (`560` used to be written out in
 * `app.css` and again in a `useNarrow` call, with nothing tying them together).
 *
 * The rule for new work: a rule about *layout shape* picks a tier. A rule that
 * sheds one specific column or re-flows one specific component at the width
 * where its own content stops fitting stays local to that component, and says
 * so in a comment — those are legitimately not tiers.
 *
 * | Tier      | px    | What changes at or below it                         |
 * |-----------|-------|-----------------------------------------------------|
 * | `phone`   | 560   | Card renderers replace tables; density tightens.     |
 * | `compact` | 720   | Rails go horizontal; charts drop a level of detail.  |
 * | `tablet`  | 768   | Below: touch shell (drawer + bottom nav). At/above:  |
 * |           |       | the icon rail, and sheets become centred cards.      |
 * | `desktop` | 1024  | At/above: the full expanded sidebar.                 |
 *
 * `tablet` and `desktop` deliberately equal `breakpoints.md` / `.lg` — the
 * shell's two structural switches are the one place the generic scale was
 * already the right answer.
 */
export const layout = {
  phone: 560,
  compact: 720,
  tablet: breakpoints.md,
  desktop: breakpoints.lg,
} as const;

export type LayoutTier = keyof typeof layout;
